import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../api/auth';

const Activate = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { verifyToken(); }, [token]);

  const verifyToken = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/auth/activate/${token}`);
      setUser(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Lien invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Le mot de passe doit faire au moins 6 caractères'); return; }
    if (password !== confirmPassword) { setError('Les 2 mots de passe ne correspondent pas'); return; }

    try {
      setSubmitting(true);
      await api.post(`/auth/set-password/${token}`, {
        password: password,
        password_confirmation: confirmPassword,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'activation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-white text-xl">Vérification du lien...</div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <p className="text-6xl mb-4">❌</p>
          <h1 className="text-2xl font-bold text-red-600 mb-2">Lien invalide</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/login" className="inline-block bg-[#F39C12] text-white px-6 py-3 rounded-lg hover:bg-[#E67E22] font-medium">Retour à la connexion</Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <p className="text-6xl mb-4">🎉</p>
          <h1 className="text-2xl font-bold text-green-600 mb-2">Compte activé !</h1>
          <p className="text-gray-600 mb-6">Redirection vers la connexion dans 3 secondes...</p>
          <Link to="/login" className="inline-block bg-[#F39C12] text-white px-6 py-3 rounded-lg hover:bg-[#E67E22] font-medium">🔐 Se connecter</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#0A0A0A]">
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#0F0F0F] overflow-hidden items-center justify-center">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#F39C12] to-[#E67E22] opacity-70 z-10"></div>
        <div className="relative z-20 p-10 text-center">
          <img src="/images/logo.png" alt="SamaEcole" className="h-20 w-auto object-contain mx-auto mb-8" />
          <h1 className="text-white font-extrabold text-4xl mb-4">
            Bienvenue sur<br /><span className="text-[#F39C12]">SamaEcole</span>
          </h1>
          <p className="text-gray-300 text-sm max-w-sm mx-auto">Définissez votre mot de passe pour activer votre compte</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-10 bg-[#0A0A0A] min-h-screen">
        <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-10">
          <div className="text-center mb-8">
            <p className="text-5xl mb-3">🔐</p>
            <h2 className="text-2xl font-bold text-gray-900">Activation de compte</h2>
            <p className="text-gray-500 text-sm mt-1">Définissez votre mot de passe</p>
          </div>

          {user && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-800 mb-1"><strong>👤 {user.name}</strong></p>
              <p className="text-xs text-blue-600 font-mono break-all">{user.email}</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">🔑 Nouveau mot de passe</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Au moins 6 caractères" required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#F39C12] focus:ring-2 focus:ring-[#F39C12]/20" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#F39C12]">{showPassword ? '🙈' : '👁️'}</button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">🔑 Confirmer</label>
              <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Répétez" required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#F39C12] focus:ring-2 focus:ring-[#F39C12]/20" />
            </div>
            <button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-[#F39C12] to-[#E67E22] text-white font-bold py-3 rounded-xl hover:shadow-lg transition disabled:opacity-50">
              {submitting ? '⏳ Activation...' : '✅ Activer mon compte'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-gray-500 hover:text-[#F39C12]">← Retour à la connexion</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Activate;